import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, UseFormSetValue } from "react-hook-form";
import { useNavigate, useSearchParams, NavigateFunction } from "react-router-dom";
import { z } from "zod";
import Modal from "components/modal/Modal";
import { Button } from "components/ui/button/Button";
import Form from "components/ui/form/Form";
import { FormInput } from "components/ui/form/FormInput";
import { LoadingOverlay } from "components/ui/loading-spinners";
import { DecryptedUser, LeadProvider, RegisterData, RegisterResponse, RegistrationRoleType, useAddPhoneMfaDeviceMutation, useGetDecryptUserQuery, useGetUserDetailsQuery, usePostRegistrationMutation } from "./registrationService";
import * as Constant from "utilities/Constant";
import { emailAddress, phone, password_input, firstname, lastname, loanpurpose } from "utilities/formSchemas";
import "./registration.scss";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "store";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import { getEmailAddress, getNhDealApplicationId, setCredentials, setLeadProvider, setUserId } from "features/authSlice";
import { ErrorType } from "utilities/error";
import { Consent } from "components/consent/Consent";
import useDesktopChecker from "hooks/useDesktopChecker";
import { setFSUserProperties, trackFSVisitedRegistrationPage, trackFSRegistrationPagePhoneLO, trackFSRegistrationPageEmailLO } from "utilities/full-story";
import { useLoProfileCardTracking } from "hooks/useLoProfileCardTracking";
import PasswordChecker from "components/password-and-confirm-password/passwordcheckerversion2/PasswordCheckerVersion2";
import homeGraphic from "assets/images/HomeGraphic.png";
import { setResendCodeDeviceId, setAuthDeviceType } from "features/mfaSlice";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import PhoneNumberInput from "components/ui/PhoneNumberInput/PhoneNumberInput";
import { RegistrationLegalConsent } from "components/registration-legal-consent/Registration-legal-consent";
import Footer from "components/footer/Footer";
import { LoanOfficerDetail, useGetLoanOfficerByLoIdQuery, useGetLoanOfficerByNhDealApplicationIdOrInquiryIdQuery } from "pages/application/applicationService";
import LoanOfficerCardV2 from "components/v2/loan-officer-card/LoanOfficerCardV2";
import LoanOfficerCard from "components/v2/loan-officer-card/LoanOfficerCard";

const formSchema = z.object({
  email: emailAddress,
  firstname: firstname.optional(),
  lastname: lastname.optional(),
  phone: phone.optional(),
  loanpurpose: loanpurpose.optional(),
  password_input,
});

const formatContent = (content: string[]) => (
  <ul className="registration-list">
    {content?.map((line) => (
      <li key={line}>{line}</li>
    ))}
  </ul>
);

const handleRegistrationSuccess = (
  data: RegisterResponse,
  dispatch: AppDispatch,
  navigate: NavigateFunction,
  setError: (code: number) => void
): void => {
  const status = data.status;
  if (status === "OTP_REQUIRED") {
    dispatch(setUserId(data.id));
    dispatch(setResendCodeDeviceId(data.deviceAuthId));
    dispatch(setAuthDeviceType(data.deviceType ?? ""));
    navigate("/registrationAuth", { replace: true });
    return;
  } else if (status.toLowerCase() === "completed") {
    dispatch(setCredentials({ ...data }));
    navigate("/my-loans", { replace: true });
    return;
  } else if (status === "DEVICE_SELECTION_REQUIRED") {
    dispatch(setUserId(data.id));
    navigate("/mfa", { replace: true });
    return;
  }
  setError(500);
};

const getSalesChannel = (firstDomainName: string): string => {
  if (firstDomainName === "retail") return "OLA";
  if (firstDomainName === "cd") return "ILA";
  return "Unknown";
};

const renderDesktopLoPanel = (
  loanOfficer: LoanOfficerDetail | null,
  isLoadingLO: boolean,
  onPhoneClick: () => void,
  onEmailClick: () => void,
  onHeadshotError: () => void
) => {
  if (loanOfficer) {
    return (
      <LoanOfficerCardV2
        loInfo={loanOfficer}
        onPhoneClick={onPhoneClick}
        onEmailClick={onEmailClick}
        onHeadshotError={onHeadshotError}
      />
    );
  }

  if (isLoadingLO) {
    return <LoanOfficerCardV2 isLoading />;
  }

  return (
    <>
      <p className="registration__text1">Better Path to Homeownership</p>
      <p className="registration__text2">Seamless, Secure, and Built for You</p>
      <p className="registration__text3">{formatContent(Constant.registrationInformation)}</p>
      <img src={homeGraphic} alt="login-graphic" className="registration__graphic" />
    </>
  );
};

const handleSubmitError = (
  error: unknown,
  navigate: NavigateFunction,
  setPhoneErrorMessage: (msg: string) => void,
  setError: (code: number) => void
): void => {
  const err = error as ErrorType;
  if (err.status === 409) {
    navigate("/", { replace: true });
    return;
  }
  if (err.status === 400 && err.data?.code === Constant.ErrorCodeInvalidMFA) {
    setPhoneErrorMessage("Enter Valid Cell Phone Number");
    return;
  }
  setError(500);
};

const trackFSUser = (emailFromStore: string | null | undefined, decryptedUserDetails?: DecryptedUser | null): void => {
  if (emailFromStore) {
    setFSUserProperties(emailFromStore);
  } else {
    setFSUserProperties(decryptedUserDetails?.em ?? "", decryptedUserDetails?.ln?.toLowerCase() ?? "");
  }
};

const needsPhoneVerification = (isPhoneRequired: boolean | undefined, phmfapending: boolean, phoneNumber: string | undefined): boolean =>
  !!(isPhoneRequired || phmfapending) && !!phoneNumber;

interface LoTrackInfo {
  fullName?: string | null;
  workEmail?: string | null;
  nmlsLicenseNumber?: string | null;
  domains?: Array<{ domainName?: string | null }> | null;
}

const trackRegistrationVisit = (data: DecryptedUser, loanOfficer: LoTrackInfo | null): void => {
  const referringUrl = document.referrer || globalThis.location.href || "Unknown";
  const firstDomainName = (loanOfficer?.domains?.[0]?.domainName ?? "").trim().toLowerCase();
  trackFSVisitedRegistrationPage({
    leadProvider: data.lpn ?? "Unknown",
    loanType: data.lp ?? "Unknown",
    referringUrl,
    salesChannel: getSalesChannel(firstDomainName),
    loFullName: loanOfficer?.fullName ?? undefined,
    loEmail: loanOfficer?.workEmail ?? undefined,
    loNmls: loanOfficer?.nmlsLicenseNumber ?? undefined,
    state: "Unknown",
    loCardVisible: !!loanOfficer,
  });
};

const submitRegistrationForm = async (
  formData: RegisterData,
  deps: {
    hasErrors: boolean;
    hasPwdError: boolean;
    isPhoneRequired: boolean | undefined;
    phmfapending: boolean;
    addPhoneMfaDevice: (arg: { email: string; phoneNumber: string }) => { unwrap: () => Promise<unknown> };
    sendRequest: (data: RegisterData) => { unwrap: () => Promise<unknown> };
    navigate: NavigateFunction;
    setPhoneErrorMessage: (msg: string) => void;
    setError: (code: number) => void;
  }
): Promise<void> => {
  if (deps.hasErrors || deps.hasPwdError) return;
  try {
    if (needsPhoneVerification(deps.isPhoneRequired, deps.phmfapending, formData.phoneNumber)) {
      await deps.addPhoneMfaDevice({ email: formData.emailaddress, phoneNumber: formData.phoneNumber as string }).unwrap();
    }
    await deps.sendRequest(formData).unwrap();
  } catch (error) {
    handleSubmitError(error, deps.navigate, deps.setPhoneErrorMessage, deps.setError);
  }
};

const buildLOTrackPayload = (loanOfficer: LoTrackInfo, lpn?: string | null) => ({
  leadProvider: lpn ?? "Unknown",
  salesChannel: getSalesChannel((loanOfficer?.domains?.[0]?.domainName ?? "").trim().toLowerCase()),
  referringUrl: document.referrer || globalThis.location.href || "Unknown",
  loFullName: loanOfficer.fullName ?? "Unknown",
  loEmail: loanOfficer.workEmail ?? "Unknown",
  loNmls: loanOfficer.nmlsLicenseNumber ?? "Unknown",
  state: "Unknown",
});

const getPixelSrc = (lpn: string | null | undefined, id: string | null | undefined): string => {
  const leadProvider = encodeURIComponent(lpn ?? "Others");
  const inquiryId = encodeURIComponent(id && id !== "00000000-0000-0000-0000-000000000000" ? id : "");
  return `${Constant.TTD_CODE_SNIPPET_URL}/?adv=ffdysrg&ct=0:vf1o9ix&fmt=3&td1=${leadProvider}&td2=&td3=${inquiryId}`;
};

const hasPixelTrigger = (d: unknown, lpn: unknown): boolean => !!(d ?? lpn);

const computeIsPhoneValid = (
  isPhoneRequired: boolean | undefined,
  phmfapending: boolean,
  phoneDigits: string
): boolean => !(isPhoneRequired || phmfapending) || phoneDigits.length === 10;

const getLoLookUpId = (nhDealAppId: string | null | undefined, inquiryId: string): string =>
  nhDealAppId && nhDealAppId !== "00000000-0000-0000-0000-000000000000" ? nhDealAppId : inquiryId;

const RegistrationErrorDisplay = ({ isDesktop }: { isDesktop: boolean }) => (
  <div className="registration__error">
    {isDesktop
      ? Constant.RegistrationError
      : Constant.RegistrationError.map((e) => <p key={e}>{e}</p>)}
  </div>
);

const processDecryptedUser = (
  data: DecryptedUser,
  leadProviderName: string,
  navigate: NavigateFunction,
  dispatch: AppDispatch,
  setValue: UseFormSetValue<z.infer<typeof formSchema>>,
  setIsBtnDisabled: (updater: (prev: boolean) => boolean) => void,
  setIsBonzoSpouse: (value: boolean) => void
): void => {
  const { ex, fn, ln, em, ph, lp, lpn, rt, phmfapending } = data;
  if (ex) {
    navigate("/", { replace: true, state: data });
    return;
  }
  if (lpn) {
    dispatch(setLeadProvider(lpn));
  }
  setIsBtnDisabled((prevState) => !prevState);
  setValue("firstname", fn);
  setValue("lastname", ln);
  setValue("email", em);
  if (!phmfapending) {
    setValue("phone", ph.replaceAll("-", ""));
  }
  setValue("loanpurpose", lp ?? undefined);
  const isBonzoSpousalCoBorrower =
    leadProviderName?.toLowerCase() === LeadProvider.Bonzo.toLowerCase() &&
    rt?.toLowerCase() === RegistrationRoleType.SpousalCoBorrower.toLowerCase();
  setIsBonzoSpouse(isBonzoSpousalCoBorrower);
};

export default function Registration() {
  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields },
    setValue,
    watch,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isDesktop = useDesktopChecker();
  const encryptedUser = searchParams.get("t")!;
  const email = searchParams.get("email")!;
  const loanId = searchParams.get("loanId") ?? searchParams.get("lid")!;
  const loanOfficerId = searchParams.get("log");
  const dealId = searchParams.get("did")!;
  const prospectId = searchParams.get("pid")!;
  const leadProviderName = searchParams.get("lpn")!;
  const losId = searchParams.get("losId")!;
  const isEncryptedUser = !!encryptedUser;
  const isBonzoUrlParams = !!(email && loanId && leadProviderName);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const emailFromStore = useSelector(getEmailAddress);
  const nhDealApplicationIdFromStore = useSelector(getNhDealApplicationId);
  const {
    data: decryptedUserDetails,
    isLoading,
    isFetching,
    isSuccess: isDecryptUserSuccess,
  } = useGetDecryptUserQuery({ encryptedUser, email, loanId, leadProviderName, dealId, losId, prospectId }, { skip: !(isEncryptedUser || isBonzoUrlParams) });

  const nhDealAppId = decryptedUserDetails?.nhDealAppId ?? nhDealApplicationIdFromStore;
  const inquiryId = decryptedUserDetails?.id ?? "";
  const loLookUpId = getLoLookUpId(nhDealAppId, inquiryId);

  // Fetch by loId first (self-reg / "log" URL param path). If present, skip the
  // nhDealApp/inquiryId lookup entirely to avoid a redundant network call.
  const { data: loDataByLoId = null, isLoading: isLoadingLoByLoId, error: loErrorByLoId } = useGetLoanOfficerByLoIdQuery(
    loanOfficerId ?? "",
    { skip: !loanOfficerId }
  );

  // Skip if: decrypt hasn't succeeded yet, no valid lookup ID exists, or LO data
  const { data: loData = null, isLoading: isLoadingLoByDeal, error: loErrorByDeal } = useGetLoanOfficerByNhDealApplicationIdOrInquiryIdQuery(loLookUpId,
    { skip: !isDecryptUserSuccess || (!inquiryId && !nhDealAppId) || !!loanOfficerId }
  );

  const rawLoData = loDataByLoId ?? loData;
  const loanOfficer = rawLoData?.fullName && rawLoData.fullName !== "" && rawLoData.fullName !== "Call Queue" ? rawLoData : null;
  const isLoadingLO = isLoading || isFetching || isLoadingLoByLoId || isLoadingLoByDeal;
  const loanOfficerError = loErrorByLoId ?? loErrorByDeal;

  const [loHeadshotLoadError, setLoHeadshotLoadError] = useState(false);
  useEffect(() => {
    setLoHeadshotLoadError(false);
  }, [loanOfficer?.photoImageUrl]);

  useLoProfileCardTracking({
    surface: "registration",
    loInfo: loanOfficer,
    isLoading: isLoadingLO,
    error: loanOfficerError,
    headshotLoadError: loHeadshotLoadError,
    dealId: nhDealAppId,
  });

  const {
    data: isPhoneRequired,
    isSuccess: isUserDetailsSuccess,
    isLoading: isUserDetailsLoading,
    isFetching: isPhoneNumberFetching,
  } = useGetUserDetailsQuery(emailFromStore || "", {
    skip: !emailFromStore,
  });

  useEffect(() => {
    if (!decryptedUserDetails) return;
    processDecryptedUser(decryptedUserDetails, leadProviderName, navigate, dispatch, setValue, setIsBtnDisabled, setIsBonzoSpouse);
  }, [decryptedUserDetails]);


  useEffect(() => {
    if (!decryptedUserDetails || decryptedUserDetails.ex) return;
    trackRegistrationVisit(decryptedUserDetails, loanOfficer);
  }, [decryptedUserDetails, loanOfficer]);

  useEffect(() => {
    if (emailFromStore) {
      setValue("email", emailFromStore);
    }
  }, [emailFromStore]);

  // Show Tracking Pixel
  const [showPixel, setShowPixel] = useState(false);
  const pixelReady = hasPixelTrigger(decryptedUserDetails, leadProviderName);
  useEffect(() => {
    if (featureFlag?.ttdCodeSnippetEnabled && !isLoading && !isFetching && pixelReady) {
      setShowPixel(true);
    }
  }, [featureFlag?.ttdCodeSnippetEnabled, isLoading, isFetching, pixelReady]);

  const showModal = !isLoading && !isFetching && !decryptedUserDetails && !emailFromStore;

  if (!showModal) {
    trackFSUser(emailFromStore, decryptedUserDetails);
  }

  const [error, setError] = useState(0);
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [hasPwdError, setHasPwdError] = useState(false);
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);
  const [isBonzoSpouse, setIsBonzoSpouse] = useState(false);
  const [sendRequest, { isSuccess, data, isLoading: isRegistrationLoading }] = usePostRegistrationMutation();
  const [addPhoneMfaDevice, { isLoading: isAddingMfaDeviceInProgress }] = useAddPhoneMfaDeviceMutation();
  const dispatch = useDispatch();

  const handleLOPhoneClick = () => {
    if (loanOfficer) trackFSRegistrationPagePhoneLO(buildLOTrackPayload(loanOfficer, decryptedUserDetails?.lpn));
  };

  const handleLOEmailClick = () => {
    if (loanOfficer) trackFSRegistrationPageEmailLO(buildLOTrackPayload(loanOfficer, decryptedUserDetails?.lpn));
  };

  const onSubmit = (data: RegisterData) => submitRegistrationForm(data, {
    hasErrors,
    hasPwdError,
    isPhoneRequired,
    phmfapending: !!decryptedUserDetails?.phmfapending,
    addPhoneMfaDevice,
    sendRequest,
    navigate,
    setPhoneErrorMessage,
    setError,
  });

  isSuccess && data && handleRegistrationSuccess(data, dispatch, navigate, setError);
  const ispasswordFieldModified = Boolean(dirtyFields.password_input);
  const hasErrors = !!Object.keys(errors).length;
  const phoneValue = watch("phone");
  const phoneDigits = phoneValue?.replaceAll(/\D/g, "") ?? "";
  const isPhoneValid = computeIsPhoneValid(isPhoneRequired, !!decryptedUserDetails?.phmfapending, phoneDigits);

  if (isRegistrationLoading || isUserDetailsLoading || isAddingMfaDeviceInProgress) return <LoadingOverlay text="Loading..." />;

  return (
    <>
      {/* NAF_Lead Portal_Static_6.4.25 Tracking Pixel */}
      {showPixel && (
        <img
          height="1"
          width="1"
          style={{ borderStyle: "none" }}
          alt=""
          src={getPixelSrc(leadProviderName || decryptedUserDetails?.lpn, decryptedUserDetails?.id)}
        />
      )}

      {(isLoading || isFetching) && <LoadingOverlay text="Loading user details..." />}
      <div className="registration">
        {showModal && (
          <Modal
            defaultBody
            defaultBodyLabel="Something went wrong!"
            defaultBodyLabelIcon={regular("triangle-exclamation")}
            defaultBodySubContent={[
              "Please open the email we sent you and click on the link so that we can try again.",
              "We apologize for the inconvenience.",
            ]}
            footer={<></>}
          />
        )}
        <div className="registration__container">
          <div className="registration__form">
            <h1 className="registration__header">{isDesktop ? `Create an account to continue your application` : <>Create an account<br />to continue</>}</h1>
            {!isDesktop && loanOfficer && (
              <>
                <div className="registration__mobile-lo-invite">
                  <p>You&#39;ve been invited to start your application.</p>
                </div>
                <div className="registration__mobile-lo-card">
                  <LoanOfficerCard 
                    loInfo={loanOfficer} 
                    hideLabel 
                    onPhoneClick={handleLOPhoneClick}
                    onEmailClick={handleLOEmailClick}
                    onHeadshotError={() => setLoHeadshotLoadError(true)}
                  />
                </div>
              </>
            )}
            {!isDesktop && !loanOfficer && isLoadingLO && (
              <div className="registration__mobile-lo-card">
                <LoanOfficerCard isLoading hideLabel />
              </div>
            )}
            {error == 500 && <RegistrationErrorDisplay isDesktop={isDesktop} />}
            {phoneErrorMessage && <div className="registration__error">{phoneErrorMessage}</div>}
            <Form
              onSubmit={handleSubmit((data) => {
                onSubmit({
                  emailaddress: data.email,
                  password: data.password_input,
                  firstname: data.firstname,
                  lastname: data.lastname,
                  loanPurpose: data.loanpurpose,
                  phoneNumber: data.phone,
                  referralSource: "",
                  referredBy: "someone",
                  inquiryId: decryptedUserDetails?.id,
                  leadProvider: isBonzoSpouse ? LeadProvider.Bonzo : decryptedUserDetails?.lpn,
                  nhDealApplicationId: nhDealApplicationIdFromStore || decryptedUserDetails?.nhDealAppId || undefined,
                  userRole: decryptedUserDetails?.rt,
                  isTestLoan: decryptedUserDetails?.isTestLoan,
                  decryptPhoneNumber: decryptedUserDetails?.ph,
                  prospectId: decryptedUserDetails?.prospectId,
                }).catch(() => { setError(500) });
              })}
            >
              <div className="row--item">
                <FormInput
                  formHandle={register("email")}
                  inputProps={{
                    name: "email",
                    placeholder: "Enter Your Email Address",
                    label: "Email",
                    errorMessage: errors.email?.message,
                    isReadOnly: true,
                  }}
                />
              </div>
              <div className={!(!isPhoneNumberFetching && isPhoneRequired && isUserDetailsSuccess) && !decryptedUserDetails?.phmfapending ? "row--hidden" : "row--item"}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <PhoneNumberInput
                      onChange={(newValue: string) => {
                        onChange(newValue);
                      }}
                      value={value ?? ""}
                      errorMessage={errors.phone?.message}
                      label="Phone Number"
                      placeholder={"(XXX) XXX-XXXX"}
                    />
                  )}
                />
              </div>
              <div className="row--item">
                <Controller
                  name="password_input"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <PasswordChecker
                      onChange={onChange}
                      checkErrors={ispasswordFieldModified}
                      createLabel="Create Password"
                      setHasPasswordError={setHasPwdError}
                      setBtnVisible={setIsBtnDisabled}
                      createPasswordPlaceholderName="Create password"
                    />
                  )}
                />
              </div>
              <Button title="Continue" className="registration__button" disabled={isBtnDisabled || !isPhoneValid} />
            </Form>
            {featureFlag?.splitConsentEnabled && <Consent text={Constant.PRIVACY_AND_TERMSOFUSE_CONSENT} />}
          </div>
          {isDesktop && (
            <div className="registration__information">
              {renderDesktopLoPanel(loanOfficer, isLoadingLO, handleLOPhoneClick, handleLOEmailClick, () => setLoHeadshotLoadError(true))}
            </div>
          )}
        </div>
      </div>
      {!featureFlag?.splitConsentEnabled && <RegistrationLegalConsent />}
      <div className="registration__footer-section"><Footer /></div>
    </>
  );
}
